import React, {useEffect, useState} from 'react'
import {Controller, useForm} from "react-hook-form";
import {useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import FormPopup from "../../../components/common/popup/form-popup";
import InputSubmit from "../../../components/common/form/input-submit";
import {Warning} from "../../../components/common/alert/banner";
import InputSelect from "../../../components/common/form/input-select";
import {useTranslation} from "react-i18next";
import {
    getQuotationExtraCosts,
    postQuotationExtraCost
} from "../../../api/quotation";
import {getCustomerConditions} from '../../../api/connection';
import {TrashIcon} from "@heroicons/react/outline";
import {getPathParam} from "../../../utils/converter";
import { EXTRA_COST, QUOTATION} from "../../../api/endpoints";
import RemovePopup from "../../../components/common/popup/remove-popup";

export default function ManageExtraCosts({isOpen, setIsOpen, quotationDetail, extraCost, setRefresh , onSetExtraCost}) {

    const {loggedCompany} = useSelector((state) => state.user);
    const {handleSubmit, control} = useForm();
    let {id} = useParams();

    const {t} = useTranslation();

    const [isLoading, setLoading] = useState(false);
    const [isConditionLoading, setConditionLoading] = useState(false);
    const [exception, setException] = useState(false);
    const [removeDetail, setRemoveDetail] = useState({});
    const [isOpenRemove, setOpenRemove] = useState(false);

    const [conditions, setConditions] = useState([]);
    const [extraCosts, setExtraCosts] = useState([]);

    const fetchExtraCosts = async () => {
        if (loggedCompany && quotationDetail !== null) {
            try {
                const res = await getQuotationExtraCosts({
                    quotation_id: quotationDetail.id,
                    params: null,
                });
                setExtraCosts(res.extra_costs);
            } catch (e) {
                setExtraCosts([]);
            }
        }
    };

    const storeCondition = async (data) => {
        if (loggedCompany) {
            setLoading(true);
            postQuotationExtraCost({quotation_id: id, data})
                .then(response => {
                    onSetExtraCost(response);
                    fetchExtraCosts();
                    setLoading(false);
                }).catch(e => {
                setException(e.message);
                setLoading(false);
            });
        }
    };



    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        async function fetchCondition() {
            setConditionLoading(true);
            if (loggedCompany && quotationDetail !== null) {
                try {
                    const response = await getCustomerConditions({
                        company_id: loggedCompany.id,
                        connection_id: quotationDetail.customer.id,
                        params: null,
                        signal,
                    });

                    if (Array.isArray(response.conditions)) {
                        setConditions(response.conditions);
                    } else {
                        setConditions([]);
                    }

                } catch (e) {
                    setConditions([]);
                } finally {
                    setConditionLoading(false);
                }
            }
        }

        fetchCondition();

        return () => {
            controller.abort();
        };
    }, [loggedCompany, quotationDetail]);

    useEffect(() => {
        fetchExtraCosts();
        // eslint-disable-next-line
    }, [loggedCompany, quotationDetail]);

    const onSubmit = async (data) => {
        await storeCondition(data);
    };

    return (

        <FormPopup title={t("app.quotations.manage_costs")} isOpen={isOpen} setIsOpen={setIsOpen}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-4">
                    <div className="grid md:grid-cols-1 lg:grid-cols-1 gap-y-2 gap-x-2 sm:grid-cols-1">
                        <Controller
                            name="condition"
                            rules={{required: true}}
                            control={control}
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                                <InputSelect
                                    label={t("app.leads.condition")}
                                    name={name}
                                    options={conditions.map(c => ({
                                        id: c.id,
                                        label: c.condition_name ?? `${c.condition_type} ${c.cost} ${c.price_type}`
                                    }))}
                                    value={value}
                                    onChange={onChange}
                                    isLoading={isConditionLoading}
                                    isDisabled={isConditionLoading}
                                    errors={error}
                                />
                            )}
                        />





                        <div className="flex before:flex-1 items-center justify-between mt-6">
                            <InputSubmit
                                isLoading={isLoading}
                                label={t("app.quotations.add_extra_cost")}
                            />
                        </div>
                    </div>
                </div>

                {exception && (
                    <Warning message={exception}/>
                )}
            </form>

            {extraCosts && extraCosts.length > 0 && (
                <div className="mt-6">
                    <h3 className="font-semibold text-lg">{t('app.quotations.selected_conditions')}</h3>
                    <table className="w-full mt-2 border-collapse border border-gray-300">
                        <thead>
                        <tr className="bg-gray-100">
                            <th className="border p-2">{t('app.quotations.extra_cost_name')}</th>
                            <th className="border p-2">{t('app.quotations.extra_cost_include_price')}</th>
                            <th className="border p-2">{t('app.quotations.extra_cost_actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {extraCosts.map((extra) => (
                            <tr key={extra.id}>
                                <td className="border p-2">{extra.condition.condition_name || 'N/A'}</td>
                                <td className="border p-2 text-center">
                                    {extra.include_in_price ? (
                                        <span className="text-green-600">✓</span>
                                    ) : (
                                        <span className="text-red-500">✕</span>
                                    )}
                                </td>
                                <td className="border p-2">
                                    <button
                                        type="button"
                                        className="text-left text-red-500"
                                        onClick={() => {
                                            const removeQuotationExtraCost = {
                                                title: t("app.quotations.extra_data.remove_extra_title"),
                                                message: t("app.quotations.extra_data.remove_extra_description"),
                                                endpoint: getPathParam([QUOTATION, id, EXTRA_COST, extra.id]),
                                            };
                                            setRemoveDetail(removeQuotationExtraCost);
                                            setOpenRemove(true);
                                        }}
                                    >

                                        <TrashIcon
                                            className="w-4 h-4 text-red-700"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            <>
                {isOpenRemove && (
                    <RemovePopup
                        isOpen={isOpenRemove}
                        setIsOpen={setOpenRemove}
                        detail={removeDetail}
                        setDetail={async (r) => {
                            await fetchExtraCosts()
                            await setRefresh(true);
                        }}
                    />
                )}
            </>

        </FormPopup>


    );
}
